import type { FilterGroup } from 'src/utils/common'
import React, { useEffect, useRef, useContext } from 'react'
import { reaction, toJS } from 'mobx'
import { observer, useLocalObservable } from 'mobx-react'
import { EditTreePageTree, EditTreePageMain } from './components'
import { appStore } from 'src/stores'
import { $api, $consts } from 'src/plugins'
import { useDomSize } from 'src/hooks'
import { getMenuFormParams, getValidBtns, checkCondition } from './helper'
import { initFilter, getSingleRule, extendRules, extendRulesAndGroups } from 'src/utils/common'
import { uuid } from 'src/utils/tools'
import { omit, set, isEqual } from 'lodash'
import 'src/styles/components/root/treePage.less'

const EditTreeContext = React.createContext<any>(undefined)

const CheckTreePage = observer(() => {
  const tree = useContext(EditTreeContext)

  return (
    <React.Fragment>
      <EditTreePageTree />
      <EditTreePageMain key={tree.selectedKeys.join('-')} />
    </React.Fragment>
  )
})

type FormData = {
  // 查询参数
  filterGroup?: FilterGroup,
}

type Props = {
  formCode: string,
  formData?: FormData
}

const EditTreePage = (props: Props) => {
  const state = useLocalObservable(() => {
    return {
      uniqClass: uuid(),
      // 树表单配置
      treeConfig: {} as any,
      setTreeConfig (config: any) {
        this.treeConfig = config
      },
      getTreeConfig (formCode: string) {
        const { OrgId } = appStore.current.headers
        const payload = initFilter({ OrgId, Keyword: formCode })
        $api['tree/getTreePageConfigInfo'](payload)
          .then((res: any) => {
            res && state.setTreeConfig(res)
            return res
          })
          .then((res: any) => {
            res && this.getTreeData()
          })
      },
      // 树数据
      treeData: [] as any,
      setTreeData (data: any[], parentTree?: any) {
        const copyData = toJS(this.treeData)
        if (!parentTree?.ID) {
          this.treeData = data.map(item => {
            const find = copyData.find((cd: any) => cd.ID === item.ID)
            return Object.assign({}, find, item)
          })
          return
        }
        const digTree = (arr: any[], target: string): any => {
          for (let i = 0, j = arr.length; i < j; i++) {
            if (arr[i].ID === target) {
              return arr[i]
            }
            if (Array.isArray(arr[i].__children__) && arr[i].__children__.length) {
              const result = digTree(arr[i].__children__, target)
              if (result) return result
            }
          }
        }
        const tree = digTree(copyData, parentTree.ID)
        set(tree, '__children__', data.map(item => {
          const find = tree.__children__?.find((cd: any) => cd.ID === item.ID)
          return Object.assign({}, find, item)
        }))
        this.treeData = copyData
      },
      getTreeData (parentTree?: any) {
        const { OrgId } = appStore.current.headers
        const { Nav_PageTree = {} } = this.treeConfig
        const {
          QUERY_API_URL,
          SORT: Sort,
          ORDER: Order,
          FILTER_ORG_TYPE: OrgType,
          IGNORE_ORG_RULE: IgnoreOrgRule
        } = Nav_PageTree
        const payload: any = initFilter({ OrgId, Sort, Order, OrgType, IgnoreOrgRule })
        // 菜单参数
        payload.MenuParameter = appStore.current.activeMenu.MENU_FORM_PARAMS
        // 表单参数
        const formParams = getMenuFormParams(appStore.current.activeMenu)
        for (const [key, value] of Object.entries(formParams)) {
          payload[key] = value
        }
        // 父树ID
        const singleRule = [getSingleRule('PARENT_ID', 1, parentTree?.ID)]
        extendRules(payload, singleRule)
        // 查询条件
        extendRulesAndGroups(payload, props.formData?.filterGroup)

        return $api['app/getDataByPost'](payload, {
          url: QUERY_API_URL
        }).then((data: any) => {
          data && state.setTreeData(data, parentTree)
        })
      },
      // 拍平后一维树数据
      get flatTreeData () {
        const flatData = (tree: any[] = [], data: any[] = []) => {
          if (Array.isArray(tree) && tree.length) {
            tree.forEach(item => {
              data.push(omit(item, ['__children__']))
              flatData(item.__children__, data)
            })
          }
        }
        const targetData: any[] = []
        flatData(toJS(this.treeData), targetData)
        return targetData
      },
      // 关键字搜索
      searchKey: '',
      setSearchKey (key: string) {
        this.searchKey = key
      },
      // 搜索过滤后的树数据
      get visibleTreeData () {
        if (!this.searchKey) return this.treeData
        return this.flatTreeData.filter(item => item.NAME.indexOf(this.searchKey) !== -1)
      },
      // 树配置按钮
      get enableBtns () {
        return getValidBtns(this.treeConfig.Nav_Btns)
      },
      /**
       * 根据选中的节点获取节点配置的按钮
       * @param btns 所配置的按钮
       * @param selectedNodes 选中的节点
       * @returns 
       */
      getShowBtns (btns: any[], selectedNodes: any[]) {
        const showBtns: any[] = []
        btns.forEach((btn: any) => {
          const selectData = selectedNodes[0]
          const isShow = checkCondition(btn.BTN_CONDITION, selectData, appStore.current.activeMenu.MENU_FORM_PARAMS)
          if (isShow) {
            // 重设按钮为新增同级的按钮
            if (btn.BTN_TYPE === $consts['TABLE/BTN_TYPE_ADD'] && btn.IS_SAME_LEVEL) {
              btn.BTN_TYPE = $consts['TABLE/BTN_TYPE_TREE_SAMELEVEL_ADD']
            }
            showBtns.push(btn)
          }
        })
        return showBtns
      },
      // 竖树默认展开项
      get defaultExpand () {
        return { defaultExpandedKeys: [this.treeData[0]?.ID] }
      },
      // 竖树选中项
      selectedKeys: [] as string[],
      setSelectedKeys (keys: string[]) {
        this.selectedKeys = keys
      },
      // 竖树选中节点
      get selectedNodes () {
        return this.flatTreeData.filter(item => this.selectedKeys.indexOf(item.ID) !== -1)
      },
      // 竖树选中节点所配置的非页面展示的按钮，显示在 Tree 头部
      get selectedNodesToolBtns () {
        if (!this.selectedNodes.length) return []

        const toolBtns = toJS(this.enableBtns)
        return this.getShowBtns(toolBtns, this.selectedNodes)
      },
      // 页面宽高，主要用 Tree 设置虚拟滚动的高度
      pageDomSize: { clientWidth: 0, clientHeight: 0 },
      setPageDomSize (size: any) {
        Object.assign(this.pageDomSize, size)
      },
    }
  })

  // 树表单配置/数据
  useEffect(() => {
    state.getTreeConfig(props.formCode)
  }, [props.formCode])

  // 获取树默认选中项
  useEffect(() => reaction(
    () => {
      const { treeConfig, treeData } = state
      const { Nav_PageTree = {} } = treeConfig
      return {
        multiCheck: Nav_PageTree.MULTI_CHECK,
        multiCheckCount: Nav_PageTree.MULTI_CHECK_COUNT,
        treeData
      }
    },
    ({ multiCheck, multiCheckCount, treeData }) => {
      const defaultSelectedKeys: string[] = []
      if (Array.isArray(treeData) && treeData.length) {
        treeData.forEach(item => {
          item?.ID && defaultSelectedKeys.push(item.ID)
        })
      }
      const newKeys = multiCheck ? (multiCheckCount ? defaultSelectedKeys.slice(0, multiCheckCount) : defaultSelectedKeys) : (defaultSelectedKeys.length ? [defaultSelectedKeys[0]] : [])
      const allKeys = state.flatTreeData.map(item => item?.ID)
      const copyKeys = toJS(state.selectedKeys)
      const currKeys = copyKeys.filter((key: string) => allKeys.indexOf(key) !== -1)
      // 无之前选中的节点时，重新设置选中项
      !currKeys.length && !isEqual(newKeys, copyKeys) && state.setSelectedKeys(newKeys)
    }
  ), [])

  // 计算页面宽高
  const refOfTreePage = useRef(null)
  const domSize = useDomSize(refOfTreePage)
  useEffect(() => {
    const { clientWidth, clientHeight } = domSize
    clientWidth && clientHeight && state.setPageDomSize(domSize)
  }, [domSize])

  return (
    <EditTreeContext.Provider value={state}>
      <div ref={refOfTreePage} className={`tree-page ${state.uniqClass}`}>
        <CheckTreePage />
      </div>
    </EditTreeContext.Provider>
  )
}

export { EditTreeContext }
export default EditTreePage